import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuth } from './useAuth';

const READ_KEY = 'notif_read_v1';

function loadReadSet() {
  try {
    const raw = localStorage.getItem(READ_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function persistReadSet(set) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {
    /* storage unavailable — read state is best-effort */
  }
}

/**
 * Realtime notification feed for the logged-in agent/admin, backed by the same
 * Convex `vendorNotifications` store the payment-alert bridge listens to.
 *
 * Read/unread is tracked client-side in localStorage (the Convex row carries no
 * read flag); "dismiss" / "clear all" delete the row via
 * acknowledgeVendorNotifications.
 */
export function useNotifications() {
  const { user } = useAuth();
  const userId = Number(user?.id || user?.user_id || 0) || null;

  const rows = useQuery(
    api.services.listVendorNotifications,
    userId ? { recipientUserId: userId, limit: 100 } : 'skip',
  );
  const ack = useMutation(api.services.acknowledgeVendorNotifications);

  const [readSet, setReadSet] = useState(loadReadSet);
  const prunedFor = useRef('');

  const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  // Drop read ids that no longer correspond to a live row so the set can't grow
  // without bound.
  useEffect(() => {
    if (!Array.isArray(rows)) return;
    const live = new Set(rows.map((r) => r.messageId));
    const signature = [...live].sort().join('|');
    if (prunedFor.current === signature) return;
    prunedFor.current = signature;
    setReadSet((prev) => {
      const next = new Set([...prev].filter((id) => live.has(id)));
      if (next.size !== prev.size) persistReadSet(next);
      return next;
    });
  }, [rows]);

  const notifications = useMemo(
    () =>
      [...list]
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .map((r) => ({
          id: r.messageId,
          title: r.title || 'Notification',
          body: r.body || '',
          data: r.data || {},
          createdAt: r.createdAt || 0,
          read: readSet.has(r.messageId),
        })),
    [list, readSet],
  );

  const unreadCount = useMemo(
    () => notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
    [notifications],
  );

  const markRead = useCallback((id) => {
    setReadSet((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistReadSet(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadSet((prev) => {
      const next = new Set(prev);
      notifications.forEach((n) => next.add(n.id));
      persistReadSet(next);
      return next;
    });
  }, [notifications]);

  const dismiss = useCallback(
    async (id) => {
      if (!userId) return;
      try {
        await ack({ recipientUserId: userId, messageIds: [id] });
      } catch {
        /* row will resurface on next sync if the delete failed */
      }
    },
    [ack, userId],
  );

  const clearAll = useCallback(async () => {
    if (!userId || !notifications.length) return;
    try {
      await ack({ recipientUserId: userId, messageIds: notifications.map((n) => n.id) });
    } catch {
      /* best-effort */
    }
  }, [ack, userId, notifications]);

  return {
    notifications,
    unreadCount,
    loading: rows === undefined && !!userId,
    markRead,
    markAllRead,
    dismiss,
    clearAll,
  };
}
